"use client"

import { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { UserContext } from "@/context/UserContext"
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { VideoCardEnhanced } from './VideoCardEnhanced'
import { toast } from 'sonner'
import { LogIn, Film, Loader2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { MediaDetailsModal } from './MediaDetailsModal'
import { useTranslations } from 'next-intl'

// 动画变体
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export function GenerationHistory({ showOnlyVideos = false }) {
  const router = useRouter()
  const { user } = useContext(UserContext)
  const [videoItems, setVideoItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [pollingTasks, setPollingTasks] = useState(new Set())
  const [selectedItem, setSelectedItem] = useState(null)
  const loadingRef = useRef(null)
  const isLoadingRef = useRef(false)
  const isLoadingMoreRef = useRef(false)

  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const PAGE_SIZE = 20

  const t = useTranslations('videocard')

  // 获取视频历史记录
  const fetchHistory = useCallback(async (page = 0) => {
    try {
      if (!user?.id) return

      const isInitialLoad = page === 0

      // 防止重复请求
      if (isInitialLoad && isLoadingRef.current) {
        console.log(`[DEBUG] Already loading initial page, skipping`);
        return;
      }
      if (!isInitialLoad && isLoadingMoreRef.current) {
        console.log(`[DEBUG] Already loading more, skipping page ${page}`);
        return;
      }

      if (isInitialLoad) {
        isLoadingRef.current = true;
        setLoading(true)
      } else {
        isLoadingMoreRef.current = true;
        setLoadingMore(true)
      }

      // 通过 API 获取视频数据
      const response = await fetch(`/api/videos/list?page=${page}&pageSize=${PAGE_SIZE}`)

      if (!response.ok) {
        throw new Error('Failed to fetch videos')
      }

      const result = await response.json()
      const videosData = result.data || []

      console.log(`[DEBUG] Page ${page}: Got ${videosData.length} videos`);

      if (isInitialLoad) {
        setVideoItems(videosData)
      } else {
        setVideoItems(prev => [...prev, ...videosData])
      }

      // 检查是否有正在处理中的视频任务
      const processingTasks = videosData
        ?.filter(video => video.status === 1)
        ?.map(video => video.task_id) || []

      setPollingTasks(new Set(processingTasks))

      // 更新hasMore状态
      const actualHasMore = result.hasMore || false;
      setHasMore(actualHasMore);
      console.log(`[DEBUG] Page ${page}: 数据数量=${videosData.length}, hasMore=${actualHasMore}`);
    } catch (error) {
      console.error('Error fetching history:', error)
      toast.error(t('failedToLoadHistory'))
      // 发生错误时也要停止加载状态
      setHasMore(false);
    } finally {
      isLoadingRef.current = false;
      isLoadingMoreRef.current = false;
      setLoading(false)
      setLoadingMore(false)
    }
  }, [user?.id, t])

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (isLoadingMoreRef.current || !hasMore) {
      console.log(`[DEBUG] loadMore blocked: isLoadingMore=${isLoadingMoreRef.current}, hasMore=${hasMore}`);
      return;
    }
    const nextPage = currentPage + 1;
    console.log(`[DEBUG] loadMore: currentPage=${currentPage}, nextPage=${nextPage}`);
    setCurrentPage(nextPage);
    fetchHistory(nextPage);
  }, [currentPage, hasMore, fetchHistory])





  useEffect(() => {
    if (user) {
      console.log(`[DEBUG] User effect triggered, resetting and fetching page 0`);
      // 重置分页状态和ref
      isLoadingRef.current = false;
      isLoadingMoreRef.current = false;
      setCurrentPage(0)
      setHasMore(true)
      setVideoItems([])
      setLoadingMore(false)
      fetchHistory(0)
    } else {
      setLoading(false)
    }
  }, [user, fetchHistory])

  // 无限滚动逻辑
  useEffect(() => {
    console.log(`[DEBUG] Infinite scroll effect: hasMore=${hasMore}, loadingMore=${loadingMore}, loading=${loading}`);

    if (!hasMore || loadingMore || loading) {
      console.log(`[DEBUG] Skipping observer setup - hasMore: ${hasMore}, loadingMore: ${loadingMore}, loading: ${loading}`);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        console.log(`[DEBUG] Intersection observed: isIntersecting=${target.isIntersecting}, loadingMore=${loadingMore}, hasMore=${hasMore}`);
        if (target.isIntersecting && !loadingMore && hasMore) {
          console.log(`[DEBUG] Triggering loadMore...`);
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '300px', // 增加提前加载的距离
        threshold: 0.1
      }
    );

    if (loadingRef.current) {
      console.log(`[DEBUG] Setting up observer on loadingRef`);
      observer.observe(loadingRef.current);
    } else {
      console.log(`[DEBUG] loadingRef.current is null`);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, loadingMore, loadMore, loading])

  // 处理详情查看
  const handleViewDetails = useCallback((item) => {
    setSelectedItem(item)
  }, [])

  // 关闭详情
  const closeDetails = useCallback(() => {
    setSelectedItem(null)
  }, [])

  // 删除视频
  const handleDeleteVideo = useCallback(async (taskId) => {
    try {
      const response = await fetch(`/api/videos/${taskId}/delete`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete video')
      }

      // 从本地状态中移除被删除的视频
      setVideoItems(prev => prev.filter(item => item.task_id !== taskId))

      toast.success(t('videoDeletedSuccess'))
    } catch (error) {
      console.error('Delete video error:', error)
      toast.error(error.message || t('deleteVideoFailed'))
      throw error
    }
  }, [t])

  // 切换视频可见性
  const handleToggleVisibility = useCallback(async (taskId, isPrivate) => {
    try {
      const response = await fetch(`/api/videos/${taskId}/visibility`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_private: isPrivate }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update visibility')
      }

      // 更新本地状态中的视频可见性
      setVideoItems(prev =>
        prev.map(item =>
          item.task_id === taskId
            ? { ...item, is_private: isPrivate }
            : item
        )
      )

      const status = isPrivate ? t('private') : t('public')
      toast.success(t('visibilityUpdateSuccess', { status }))
    } catch (error) {
      console.error('Toggle visibility error:', error)
      toast.error(error.message || t('visibilityUpdateFailed'))
      throw error
    }
  }, [t])

  // 渲染空历史状态
  const renderEmptyState = () => {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <Film className="w-12 h-12 text-emerald-500/40 mb-4" />
        <p className="text-gray-400 mb-2">{t('noVideosInHistoryYet')}</p>
        <button
          onClick={() => router.push("/wan22")}
          className="mt-4 px-6 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium text-sm hover:opacity-90 transition-opacity flex items-center gap-2"
        >
          {t('createAVideo')}
        </button>
      </div>
    )
  }

  // 渲染加载状态
  const renderLoadingState = () => (
    <div className="h-full flex flex-col items-center justify-center py-12">
      <div className="w-16 h-16 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin mb-4" />
      <p className="text-emerald-400 font-medium">{t('loadingYourHistory')}</p>
    </div>
  )

  // 渲染无限滚动触发器
  const renderScrollTrigger = () => {
    // 如果没有更多数据，就不显示任何内容
    if (!hasMore && !loadingMore) {
      return null;
    }

    return (
      <div className="flex justify-center py-6">
        {loadingMore ? (
          <div className="flex items-center gap-2 text-emerald-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('loadingMoreVideos')}
          </div>
        ) : hasMore ? (
          <div
            ref={loadingRef}
            className="h-4 w-full"
            style={{ minHeight: '1px' }}
          />
        ) : null}
      </div>
    )
  }

  // 获取要显示的视频项目 - 显示所有视频（包括处理中的）
  const getFilteredItems = () => {
    return videoItems // 显示所有视频，包括处理中和已完成的
  }

  // 响应式网格配置 - 移动端一行一个，PC端自适应
  const getGridConfig = () => {
    return {
      // 使用 CSS Grid 响应式类名而不是内联样式
      className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6'
    };
  }

  // 渲染内容区域
  const renderContent = () => {
    const items = getFilteredItems()

    if (loading) {
      return renderLoadingState()
    }

    if (items.length === 0) {
      return renderEmptyState()
    }

    const gridConfig = getGridConfig();

    return (
      <>
        <motion.div
          className={`${gridConfig.className} p-3 sm:p-4 lg:p-6`}
          variants={container}
          initial="hidden"
          animate="show"
        >
          {items.map((item, index) => {
            return (
              <motion.div
                key={`${item.task_id}-${item.id || ''}-${index}`}
                className="cursor-pointer transform hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(16,185,129,0.2)] transition-all duration-300"
                onClick={() => handleViewDetails(item)}
                variants={item}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 }
                }}
              >
                <VideoCardEnhanced
                  item={item}
                  isProcessing={item.status === 1}
                  hasAccess={user?.has_access}
                  showLoading={true}
                  onViewDetails={handleViewDetails}
                  onDelete={handleDeleteVideo}
                  onToggleVisibility={handleToggleVisibility}
                  isOwner={true} // 在history页面，用户总是拥有者
                />
              </motion.div>
            );
          })}
        </motion.div>
        {renderScrollTrigger()}
      </>
    )
  }

  if (!user) {
    return (
      <div className="h-full p-6">
        <div className="rounded-xl border border-emerald-500/20 p-8 text-center bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(16,185,129,0.1)]">
          <h3 className="text-xl font-semibold mb-4 bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
            {t('accessYourCreationHistory')}
          </h3>
          <p className="text-gray-400 mb-6">
            {t('signInToViewAndManage')}
          </p>
          <button
            onClick={() => router.push("/signin")}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-emerald-500 to-green-500 text-white hover:opacity-90 h-10 px-6 py-2 shadow-[0_0_10px_rgba(16,185,129,0.3)]"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {t('signIn')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black/95">
      {user.is_guest && (
        <div className="px-6 py-4 bg-emerald-500/5 border-b border-emerald-500/10">
          <div className="flex items-center justify-between max-w-[800px] mx-auto">
            <div className="flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-emerald-400"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p className="text-sm text-emerald-400/80">
                {t('guestAccount')}
              </p>
            </div>
            <button
              onClick={() => router.push("/signin")}
              className="text-sm px-4 py-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors duration-200"
            >
              {t('signInToKeepHistory')}
            </button>
          </div>
        </div>
      )}
      <div>


        {/* 内容区域 */}
        <div>
          {renderContent()}
        </div>
      </div>

      {/* 使用新的 MediaDetailsModal 组件 */}
      {selectedItem && <MediaDetailsModal
        selectedItem={selectedItem}
        onClose={closeDetails}
        source="history"
        currentUser={user}
      />}
    </div>
  )
} 